import { useState } from "react";
import { streamPost } from "../lib/api";
import { CopyBtn } from "../components/CopyBtn";
import { Bookmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const genId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export const Recap = () => {
  const [transcript, setTranscript] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!transcript.trim()) { toast.error("paste your session first"); return; }
    setOut(""); setBusy(true);
    track("recap_submitted", { transcript_length: transcript.length });
    try {
      await streamPost("/recap", { session_id: genId(), transcript },
        (_c, full) => setOut(full),
        () => { setBusy(false); track("recap_completed"); }
      );
    } catch (e) {
      toast.error("recap failed: " + e.message);
      track("recap_failed", { error: e.message });
      setBusy(false);
    }
  };

  return (
    <div data-testid="recap-page" className="mx-auto max-w-5xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.recap</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">save your seat</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Ending a session? Paste your conversation and we&apos;ll compile a single &ldquo;resume prompt&rdquo; you can drop into
          ANY AI next time to pick up exactly where you left off.
        </p>
      </div>

      <div className="border border-white/10">
        <div className="p-6 border-b border-white/10 bg-[#0a0a0a]">
          <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// session transcript</label>
          <textarea
            data-testid="recap-input"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder={"paste your conversation with the AI here.\ninclude questions you asked, answers you liked, decisions you made, style you settled on..."}
            rows={10}
            className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none p-4 text-sm resize-none"
            style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
          />
          <button
            data-testid="recap-generate-btn"
            onClick={run}
            disabled={busy}
            className="mt-4 inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
            style={{ transitionProperty: "background-color, opacity" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
            {busy ? "generating..." : "generate resume prompt"}
          </button>
        </div>

        <div className="p-6 bg-[#050505] min-h-[300px]">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// resume prompt</label>
            {out && !busy && <CopyBtn text={out} testId="recap-copy" label="copy resume prompt" />}
          </div>
          {!out && !busy && <div className="text-neutral-600 text-sm border border-dashed border-white/10 p-8 text-center">your resume prompt will appear here</div>}
          {(out || busy) && (
            <pre data-testid="recap-output" className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-200">
{out}{busy && <span className="cursor" />}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
